import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { InventoryItem } from '@/supabase/inventory/types/inventory';

export interface BulkDeleteItemsDialogHandle {
  open: (items: InventoryItem[]) => void;
}

interface BulkDeleteItemsDialogContainerProps {
  softDeleteItems: (ids: number[]) => Promise<boolean>;
  isSubmitting: boolean;
}

// 이름 미리보기는 3개까지 — 수십 개 선택 시 다이얼로그가 이름 목록으로 페이지를 삼키지
// 않게 한다. 나머지는 '외 N개'.
const PREVIEW_NAME_COUNT = 3;

/**
 * 선택 상품 일괄 삭제 확인 다이얼로그 — DeleteItemDialogContainer(단일 삭제)와 동일 계약의
 * 다중 버전. 대상 목록은 `open(items)` 인자로만 시딩되고 imperative handle은 `open`만 노출 →
 * 부모는 ref 호출만 하므로 다이얼로그 오픈 시 부모가 재렌더되지 않는다.
 *
 * AlertDialogAction은 내부적으로 DialogPrimitive.Close라 클릭 시 자동으로 onOpenChange(false)를
 * 호출한다 — 삭제 실패 시 "열린 채 유지"를 지키려면 preventDefault()로 자동 닫힘을 막고
 * 성공했을 때만 수동 setIsOpen(false)한다(단일 삭제와 동일).
 *
 * ⚠️ React.memo로 감싸지 말 것. isSubmitting은 제출 중 버튼 비활성화(중복 제출 가드)를
 *    실시간으로 반영해야 하는 부모 live prop이다. 다른 모달 컨테이너 선례와 동일하게
 *    "open-state를 내부에 격리한 컨테이너는 항상 memo 금지"로 통일.
 */
export const BulkDeleteItemsDialogContainer = forwardRef<
  BulkDeleteItemsDialogHandle,
  BulkDeleteItemsDialogContainerProps
>(({ softDeleteItems, isSubmitting }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<InventoryItem[]>([]);
  // 진행 중이던 제출(await)이 끝나기 전에 다이얼로그가 닫혔다가 다른 대상으로
  // 재오픈되면, 그 응답이 새 세션의 상태를 건드리지 않도록 세대를 구분한다.
  const sessionRef = useRef(0);

  const open = useCallback<BulkDeleteItemsDialogHandle['open']>((nextItems) => {
    if (nextItems.length === 0) return;
    sessionRef.current += 1;
    setItems(nextItems);
    setIsOpen(true);
  }, []);

  useImperativeHandle(ref, () => ({ open }), [open]);

  const handleOpenChange = useCallback((next: boolean) => {
    if (!next) {
      sessionRef.current += 1;
      setItems([]);
    }
    setIsOpen(next);
  }, []);

  const handleConfirm = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (items.length === 0) return;
      const session = sessionRef.current;
      const success = await softDeleteItems(items.map((item) => item.id));
      if (session !== sessionRef.current) return;
      if (success) {
        setItems([]);
        setIsOpen(false);
      }
    },
    [items, softDeleteItems],
  );

  const previewNames = items.slice(0, PREVIEW_NAME_COUNT).map((item) => `'${item.name}'`);
  const restCount = items.length - PREVIEW_NAME_COUNT;

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="break-keep">
            선택한 상품 {items.length.toLocaleString('ko-KR')}개 삭제
          </AlertDialogTitle>
          <AlertDialogDescription className="break-keep">
            {previewNames.join(', ')}
            {restCount > 0 && ` 외 ${restCount.toLocaleString('ko-KR')}개`}
            <br />
            재고 기록은 남아 있으며, 같은 이름으로 다시 등록하면 복원됩니다.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>취소</AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 text-white hover:bg-red-700"
            disabled={isSubmitting || items.length === 0}
            onClick={handleConfirm}
          >
            삭제
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
});

BulkDeleteItemsDialogContainer.displayName = 'BulkDeleteItemsDialogContainer';
